import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from './ui/tabs';
import { toast } from 'sonner';
import {
  History, Mail, Download, PenLine, RefreshCw, CheckCircle2,
  XCircle, Eye, AlertTriangle, RotateCcw, Bell, Loader2, Send,
} from 'lucide-react';
import { getOfferAnnexureHistory } from '../api';

const KIND_META = {
  annexure_email: {
    label: 'Email',
    icon: Mail,
    badgeClass: 'bg-blue-50 text-blue-700 border-blue-200',
    iconClass: 'text-blue-600 bg-blue-100',
  },
  annexure_download: {
    label: 'Download',
    icon: Download,
    badgeClass: 'bg-slate-50 text-slate-700 border-slate-200',
    iconClass: 'text-slate-600 bg-slate-100',
  },
  esign: {
    label: 'E-Sign',
    icon: PenLine,
    badgeClass: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
    iconClass: 'text-fuchsia-600 bg-fuchsia-100',
  },
};

const ESIGN_ACTION_ICON = {
  sent: Send,
  bulk_sent: Send,
  reminded: Bell,
  recalled: RotateCcw,
  status_refresh: RefreshCw,
  downloaded_signed_pdf: Download,
};

const STATUS_TONE = {
  SENT: 'text-blue-700',
  VIEWED: 'text-indigo-700',
  SIGNED: 'text-emerald-700',
  DECLINED: 'text-red-700',
  EXPIRED: 'text-orange-700',
  RECALLED: 'text-zinc-700',
  DRAFT: 'text-amber-700',
};

function fmtTs(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

function relativeTime(iso) {
  if (!iso) return '';
  try {
    const t = new Date(iso).getTime();
    const diff = Date.now() - t;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  } catch { return ''; }
}

function humaniseAction(action) {
  if (!action) return '—';
  const map = {
    email: 'Emailed Annexure',
    download: 'Downloaded Annexure',
    sent: 'Sent for E-Signature',
    bulk_sent: 'Bulk-Sent for E-Signature',
    reminded: 'Reminder Sent',
    recalled: 'Request Recalled',
    status_refresh: 'Status Refreshed',
    downloaded_signed_pdf: 'Signed PDF Downloaded',
    orphan_webhook: 'Orphan Webhook',
  };
  if (map[action]) return map[action];
  if (action.startsWith('webhook_')) {
    return `Zoho webhook: ${action.replace('webhook_', '').replace(/_/g, ' ')}`;
  }
  return action.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function AnnexureHistoryDialog({ open, onOpenChange, offer, initialFilter = 'all' }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState(initialFilter);

  const load = useCallback(async () => {
    if (!offer?.id) return;
    setLoading(true);
    try {
      const { data } = await getOfferAnnexureHistory(offer.id);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [offer?.id]);

  useEffect(() => {
    if (open && offer) {
      setFilter(initialFilter || 'all');
      load();
    }
  }, [open, offer, initialFilter, load]);

  const timeline = data?.timeline || [];

  const summary = useMemo(() => {
    const out = { total: timeline.length, email: 0, download: 0, esign: 0, lastEvent: null };
    for (const t of timeline) {
      if (t.kind === 'annexure_email') out.email++;
      else if (t.kind === 'annexure_download') out.download++;
      else if (t.kind === 'esign') out.esign++;
    }
    out.lastEvent = timeline[0]?.at || null;
    return out;
  }, [timeline]);

  const filtered = useMemo(() => {
    if (filter === 'all') return timeline;
    if (filter === 'email') return timeline.filter((t) => t.kind === 'annexure_email');
    if (filter === 'download') return timeline.filter((t) => t.kind === 'annexure_download');
    if (filter === 'esign') return timeline.filter((t) => t.kind === 'esign');
    return timeline;
  }, [timeline, filter]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="annexure-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History size={20} className="text-indigo-600" />
            Annexure History — {data?.candidate_name || offer?.candidate_name || 'Offer Letter'}
          </DialogTitle>
          <DialogDescription>
            Full audit trail of annexure activity: emails sent, downloads, and e-signing events.
          </DialogDescription>
        </DialogHeader>

        {/* Summary strip */}
        <div className="grid grid-cols-4 gap-2 border-b pb-3">
          <SummaryCard label="Total" value={summary.total} testid="hist-total" />
          <SummaryCard label="Emails" value={summary.email} color="text-blue-700" testid="hist-emails" />
          <SummaryCard label="Downloads" value={summary.download} color="text-slate-700" testid="hist-downloads" />
          <SummaryCard label="E-Sign" value={summary.esign} color="text-fuchsia-700" testid="hist-esign" />
        </div>
        {summary.lastEvent && (
          <div className="text-xs text-slate-500 -mt-1">
            Last activity: <span className="font-medium text-slate-700">{fmtTs(summary.lastEvent)}</span>{' '}
            ({relativeTime(summary.lastEvent)})
          </div>
        )}

        {/* Filter tabs */}
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="all" data-testid="hist-tab-all">All</TabsTrigger>
            <TabsTrigger value="email" data-testid="hist-tab-email">Email</TabsTrigger>
            <TabsTrigger value="download" data-testid="hist-tab-download">Download</TabsTrigger>
            <TabsTrigger value="esign" data-testid="hist-tab-esign">E-Sign</TabsTrigger>
          </TabsList>
          <TabsContent value={filter} className="mt-3">
            {loading && (
              <div className="flex items-center justify-center py-8 text-slate-500">
                <Loader2 className="animate-spin mr-2" size={18} />
                Loading history...
              </div>
            )}
            {!loading && filtered.length === 0 && (
              <div className="text-center py-8 text-slate-500 text-sm">
                No activity yet. Activity will appear here once you email, download, or e-sign the annexure.
              </div>
            )}
            {!loading && filtered.length > 0 && (
              <ol className="relative border-l-2 border-slate-200 pl-6 space-y-4 max-h-[400px] overflow-y-auto pr-2">
                {filtered.map((evt, idx) => (
                  <TimelineRow key={evt.id || idx} evt={evt} testid={`hist-row-${idx}`} />
                ))}
              </ol>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="hist-refresh-btn">
            <RefreshCw size={14} className="mr-1.5" /> Refresh
          </Button>
          <Button onClick={() => onOpenChange(false)} variant="outline" data-testid="hist-close-btn">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SummaryCard({ label, value, color = 'text-slate-900', testid }) {
  return (
    <div className="rounded-lg border bg-slate-50 px-3 py-2">
      <div className={`text-xl font-bold ${color}`} data-testid={testid}>{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
    </div>
  );
}

function TimelineRow({ evt, testid }) {
  const meta = KIND_META[evt.kind] || KIND_META.esign;
  let RowIcon = meta.icon;
  if (evt.kind === 'esign' && ESIGN_ACTION_ICON[evt.action]) {
    RowIcon = ESIGN_ACTION_ICON[evt.action];
  }
  const isFailure = evt.success === false;
  return (
    <li className="relative" data-testid={testid}>
      {/* Dot */}
      <span
        className={`absolute -left-[34px] flex items-center justify-center w-7 h-7 rounded-full ring-4 ring-white ${meta.iconClass}`}
      >
        <RowIcon size={14} />
      </span>
      <div className="flex items-start justify-between gap-2">
        <div className="space-y-0.5 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm text-slate-800">
              {humaniseAction(evt.action)}
            </span>
            <Badge variant="outline" className={`text-[10px] py-0 ${meta.badgeClass}`}>
              {meta.label}
            </Badge>
            {evt.status && (
              <Badge variant="outline" className={`text-[10px] py-0 ${STATUS_TONE[evt.status] || 'text-slate-700'}`}>
                {evt.status}
              </Badge>
            )}
            {isFailure && (
              <Badge variant="outline" className="text-[10px] py-0 bg-red-50 text-red-700 border-red-200">
                <AlertTriangle size={10} className="mr-0.5" /> Failed
              </Badge>
            )}
            {evt.success === true && evt.kind === 'annexure_email' && (
              <Badge variant="outline" className="text-[10px] py-0 bg-emerald-50 text-emerald-700 border-emerald-200">
                <CheckCircle2 size={10} className="mr-0.5" /> Sent
              </Badge>
            )}
          </div>
          {evt.recipient && (
            <div className="text-xs text-slate-600 truncate">
              <span className="font-medium">To:</span> {evt.recipient}
              {Array.isArray(evt.cc) && evt.cc.length > 0 && (
                <span className="text-slate-400"> · CC: {evt.cc.join(', ')}</span>
              )}
            </div>
          )}
          {evt.actor_name && (
            <div className="text-xs text-slate-500">
              by {evt.actor_name}
            </div>
          )}
          {evt.notes && (
            <div className="text-xs italic text-slate-500 truncate">"{evt.notes}"</div>
          )}
          {evt.error && (
            <div className="text-xs text-red-600 truncate">{evt.error}</div>
          )}
          {evt.request_id && (
            <div className="text-[10px] text-slate-400 truncate">
              Zoho ID: <code>{evt.request_id}</code>
            </div>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="text-xs font-medium text-slate-700">{relativeTime(evt.at)}</div>
          <div className="text-[10px] text-slate-400">{fmtTs(evt.at)}</div>
        </div>
      </div>
    </li>
  );
}
